import React, { useState } from 'react';
import { useMutation, useQuery } from '@/apollo';
import { useNavigate } from '@tanstack/react-router';
import { SETUP_DATABASE, GET_ENVIRONMENT_STATUS, SETUP_AVAILABILITY } from '../../graphql/setup';
import { Button } from '../ui/Button';
import { CheckCircle, XCircle, AlertCircle, Database, Shield, Globe, Server, Key, Lock } from 'lucide-react';

const SetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [setupComplete, setSetupComplete] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [setupSecret, setSetupSecret] = useState('');

  const { data: availabilityData, loading: availabilityLoading } = useQuery(SETUP_AVAILABILITY);
  const setupAvailability = availabilityData?.setupAvailability;
  const secretReady = !setupAvailability?.requiresSecret || setupSecret.trim().length > 0;

  const { data: envStatus, loading: envLoading, error: envError } = useQuery(GET_ENVIRONMENT_STATUS, {
    variables: { setupSecret: setupSecret.trim() || null },
    skip: !setupAvailability?.available || !secretReady,
  });
  const [setupDatabase, { loading: setupLoading }] = useMutation(SETUP_DATABASE);

  const handleSetup = async () => {
    try {
      setSetupError(null);
      const result = await setupDatabase({
        variables: { setupSecret: setupSecret.trim() || null },
      });
      
      if (result.data?.setupDatabase?.success) {
        setSetupComplete(true);
      } else {
        setSetupError(result.data?.setupDatabase?.message || 'Setup failed');
      }
    } catch (error) {
      setSetupError(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'working':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'disabled':
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'working':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'error':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'disabled':
        return `${baseClasses} bg-gray-100 text-gray-600`;
      default:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
    }
  };

  const isSetupDisabled = () => {
    if (!setupAvailability?.available) return true;
    if (setupAvailability.requiresSecret && !setupSecret.trim()) return true;
    if (!envStatus?.getEnvironmentStatus) return true;
    const { database, jwt, cors } = envStatus.getEnvironmentStatus;
    return database.status === 'error' || jwt.status === 'error' || cors.status === 'error';
  };

  if (!availabilityLoading && setupAvailability && !setupAvailability.available) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
        <div className="card max-w-lg w-full p-8 text-center">
          <Lock className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
          <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Setup Not Available
          </h1>
          <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
            First-run setup has already been completed or is disabled on this environment.
          </p>
          <Button onClick={() => navigate({ to: '/' })} className="w-full">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
        <div className="card max-w-2xl w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Setup Complete!
          </h1>
          <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
            Your database has been successfully initialized with default data.
          </p>
          <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
            <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Created Accounts:
            </h3>
            <ul className="text-left space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
              <li>• SuperAdmin - Username: SuperAdmin, Password: admin123</li>
              <li>• Test User - Username: testuser, Password: password123</li>
              <li>• Test Moderator - Username: testmoderator, Password: password123</li>
            </ul>
          </div>
          <Button
            onClick={() => navigate({ to: '/' })}
            className="w-full"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
      <div className="card max-w-4xl w-full p-8">
        <div className="text-center mb-8">
          <Database className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-accent)' }} />
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            Database Setup
          </h1>
          <p className="text-lg" style={{ color: 'var(--color-text-secondary)' }}>
            Initialize your database with default data and verify environment configuration
          </p>
        </div>

        {setupAvailability?.requiresSecret && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Lock className="w-5 h-5 mr-2" />
              Setup Secret
            </h2>
            <input
              type="password"
              value={setupSecret}
              onChange={(e) => setSetupSecret(e.target.value)}
              placeholder="Enter setup secret from server configuration"
              className="w-full px-4 py-3 border rounded-lg"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
              }}
              autoComplete="off"
            />
            <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
              SETUP_SECRET is configured on this server. Enter it to view environment status and run setup.
            </p>
          </div>
        )}

        {/* Environment Status Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
            <Server className="w-5 h-5 mr-2" />
            Environment Status
          </h2>
          
          {setupAvailability?.requiresSecret && !secretReady && (
            <div className="text-center py-4" style={{ color: 'var(--color-text-secondary)' }}>
              Enter the setup secret above to load environment status.
            </div>
          )}

          {envLoading && secretReady && (
            <div className="text-center py-4" style={{ color: 'var(--color-text-secondary)' }}>
              Loading environment status...
            </div>
          )}
          
          {envError && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200">
              <p className="text-red-800">Failed to load environment status: {envError.message}</p>
            </div>
          )}
          
          {envStatus?.getEnvironmentStatus && (
            <div className="grid gap-4 md:grid-cols-2">
              {/* Database Status */}
              <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Database className="w-5 h-5 mr-2" style={{ color: 'var(--color-text-secondary)' }} />
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Database</span>
                  </div>
                  {getStatusIcon(envStatus.getEnvironmentStatus.database.status)}
                </div>
                <div className="flex items-center justify-between">
                  <span className={getStatusBadge(envStatus.getEnvironmentStatus.database.status)}>
                    {envStatus.getEnvironmentStatus.database.status}
                  </span>
                </div>
                {envStatus.getEnvironmentStatus.database.message && (
                  <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                    {envStatus.getEnvironmentStatus.database.message}
                  </p>
                )}
              </div>

              {/* JWT Status */}
              <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 mr-2" style={{ color: 'var(--color-text-secondary)' }} />
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>JWT Secrets</span>
                  </div>
                  {getStatusIcon(envStatus.getEnvironmentStatus.jwt.status)}
                </div>
                <div className="flex items-center justify-between">
                  <span className={getStatusBadge(envStatus.getEnvironmentStatus.jwt.status)}>
                    {envStatus.getEnvironmentStatus.jwt.status}
                  </span>
                </div>
                {envStatus.getEnvironmentStatus.jwt.message && (
                  <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                    {envStatus.getEnvironmentStatus.jwt.message}
                  </p>
                )}
              </div>

              {/* CORS Status */}
              <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 mr-2" style={{ color: 'var(--color-text-secondary)' }} />
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>CORS Origin</span>
                  </div>
                  {getStatusIcon(envStatus.getEnvironmentStatus.cors.status)}
                </div>
                <div className="flex items-center justify-between">
                  <span className={getStatusBadge(envStatus.getEnvironmentStatus.cors.status)}>
                    {envStatus.getEnvironmentStatus.cors.status}
                  </span>
                </div>
                {envStatus.getEnvironmentStatus.cors.message && (
                  <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                    {envStatus.getEnvironmentStatus.cors.message}
                  </p>
                )}
              </div>

              {/* Entra ID Status */}
              <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Key className="w-5 h-5 mr-2" style={{ color: 'var(--color-text-secondary)' }} />
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Entra ID</span>
                  </div>
                  {getStatusIcon(envStatus.getEnvironmentStatus.entra.status)}
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className={getStatusBadge(envStatus.getEnvironmentStatus.entra.status)}>
                    {envStatus.getEnvironmentStatus.entra.status}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {envStatus.getEnvironmentStatus.entra.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {envStatus.getEnvironmentStatus.entra.message && (
                  <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                    {envStatus.getEnvironmentStatus.entra.message}
                  </p>
                )}
              </div>

              {/* Environment Info */}
              <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center mb-2">
                  <Server className="w-5 h-5 mr-2" style={{ color: 'var(--color-text-secondary)' }} />
                  <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Environment</span>
                </div>
                <div className="space-y-1">
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    <span className="font-medium">NODE_ENV:</span> {envStatus.getEnvironmentStatus.nodeEnv}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    <span className="font-medium">PORT:</span> {envStatus.getEnvironmentStatus.port}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Database Connection Details */}
        {envStatus?.getEnvironmentStatus?.databaseConnection && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
              <Database className="w-5 h-5 mr-2" />
              Database Connection Details
            </h2>
            <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}>
              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>Server</p>
                  <p className="text-sm font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                    {envStatus.getEnvironmentStatus.databaseConnection.server}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>Database</p>
                  <p className="text-sm font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                    {envStatus.getEnvironmentStatus.databaseConnection.database}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>User</p>
                  <p className="text-sm font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                    {envStatus.getEnvironmentStatus.databaseConnection.user}
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Connection Status</span>
                  <div className="flex items-center">
                    {getStatusIcon(envStatus.getEnvironmentStatus.databaseConnection.status)}
                    <span className={`ml-2 ${getStatusBadge(envStatus.getEnvironmentStatus.databaseConnection.status)}`}>
                      {envStatus.getEnvironmentStatus.databaseConnection.status}
                    </span>
                  </div>
                </div>
                {envStatus.getEnvironmentStatus.databaseConnection.message && (
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    {envStatus.getEnvironmentStatus.databaseConnection.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Setup Button */}
        <div className="text-center">
          <Button
            onClick={handleSetup}
            disabled={isSetupDisabled() || setupLoading}
            className="w-full max-w-md"
          >
            {setupLoading ? 'Setting up database...' : 'Setup Database'}
          </Button>
          
          {isSetupDisabled() && (
            <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>
              Please fix environment configuration issues before proceeding
            </p>
          )}
        </div>

        {/* Error Display */}
        {setupError && (
          <div className="mt-6 p-4 rounded-lg bg-red-50 border border-red-200">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-red-800 font-medium">Setup Error</p>
            </div>
            <p className="text-red-700 mt-1">{setupError}</p>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-8 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
          <h3 className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            What this setup will create:
          </h3>
          <ul className="space-y-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            <li>• Application configuration settings</li>
            <li>• SuperAdmin user account (admin123)</li>
            <li>• Test user accounts (password123)</li>
            <li>• US states data (51 states)</li>
          </ul>
        </div>
        </div>
    </div>
  );
};

export default SetupPage;
